import { useState, useMemo } from 'react';
import { Button, SimpleSelect, SimpleSelectOption, SlidingPane, StyledFlex, Tab, TabList, TabPanel } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { faBarsSort, faRoute, faFlag } from '@fortawesome/pro-regular-svg-icons';
import { RoomType } from 'src/common/enums/rooms';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useTrackEventMutation } from 'src/common/slices';
import { RoomsList } from '../rooms-list';
import { sortRooms } from './skill-details.helpers';
import { StyledFilterHeader, StyledSkillDetailsHeader, StyledTab, StyledTabsFilterWrapper, StyledTypeButton } from './skill-details.styles';
import { SkillDetailsProps } from './skill-details.types';
import { ROOMS_ORDER_OPTIONS } from './skill-details.constants';
export const SkillDetails = ({
  isSkillDetailsOpen,
  setIsSkillDetailsOpen,
  rooms,
  skillSelected,
  isFetching,
  setSkillSelected
}: SkillDetailsProps) => {
  const toCompleteRooms = rooms?.filter(room => room.progress < 100);
  const [selectedOrder, setSelectedOrder] = useState<SimpleSelectOption>({
    label: 'Newest',
    value: 'newest'
  });
  const [selectedType, setSelectedType] = useState<RoomType | 'all'>('all');
  const isNewRoomsEnabled = useFeatureIsOn('skills-matrix-new-rooms');
  const handleCloseDetail = () => {
    setIsSkillDetailsOpen(false);
    setSkillSelected();
  };
  const sortedRooms = useMemo(() => sortRooms(rooms ?? [], selectedOrder?.value), [rooms, selectedOrder?.value]);
  const sortedToCompleteRooms = useMemo(() => sortRooms(toCompleteRooms ?? [], selectedOrder?.value), [toCompleteRooms, selectedOrder?.value]);
  const filteredRooms = useMemo(() => {
    if (!sortedRooms) return [];
    if (selectedType === 'all') return sortedRooms;
    return sortedRooms.filter(room => room.type === selectedType);
  }, [sortedRooms, selectedType]);
  const filteredToCompleteRooms = useMemo(() => {
    if (!sortedToCompleteRooms) return [];
    if (selectedType === 'all') return sortedToCompleteRooms;
    return sortedToCompleteRooms.filter(room => room.type === selectedType);
  }, [sortedToCompleteRooms, selectedType]);
  const [trackEvent] = useTrackEventMutation();
  const handleFilterChange = (value: string) => {
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'filter-change-skills-matrix',
      properties: {
        filter: value,
        skill_name: skillSelected?.label
      }
    });
  };
  const handleSelectedTypeChange = (value: string) => {
    setSelectedType((value as RoomType));
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'filter-change-skills-matrix',
      properties: {
        filter: value,
        skill_name: skillSelected?.label
      }
    });
  };
  const handleSelectedOrderChange = (option: SimpleSelectOption) => {
    setSelectedOrder(option);
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'filter-change-skills-matrix',
      properties: {
        filter: option.label,
        skill_name: skillSelected?.label
      }
    });
  };
  return <SlidingPane isSlidingPaneOpen={isSkillDetailsOpen} setIsSlidingPaneOpen={setIsSkillDetailsOpen} onClose={handleCloseDetail} data-sentry-element="SlidingPane" data-sentry-component="SkillDetails" data-sentry-source-file="skill-details.tsx">
      <StyledSkillDetailsHeader data-sentry-element="StyledSkillDetailsHeader" data-sentry-source-file="skill-details.tsx">
        <div>
          <h3>{skillSelected?.label}</h3>
          <p>Complete these rooms to up your skills</p>
        </div>
        <Button variant="text" onClick={handleCloseDetail} data-sentry-element="Button" data-sentry-source-file="skill-details.tsx">
          <FontAwesomeIcon icon={faClose} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="skill-details.tsx" />
        </Button>
      </StyledSkillDetailsHeader>
      <div>
        <StyledTab defaultValue="all" onValueChange={handleFilterChange} data-sentry-element="StyledTab" data-sentry-source-file="skill-details.tsx">
          <StyledFilterHeader data-sentry-element="StyledFilterHeader" data-sentry-source-file="skill-details.tsx">
            <StyledTabsFilterWrapper data-sentry-element="StyledTabsFilterWrapper" data-sentry-source-file="skill-details.tsx">
              <TabList data-sentry-element="TabList" data-sentry-source-file="skill-details.tsx">
                <Tab value="all" data-sentry-element="Tab" data-sentry-source-file="skill-details.tsx">All rooms</Tab>
                <Tab value="to-complete" data-sentry-element="Tab" data-sentry-source-file="skill-details.tsx">Incomplete rooms</Tab>
              </TabList>
              <span data-testid="rooms-select-order">
                <SimpleSelect options={ROOMS_ORDER_OPTIONS} icon={<FontAwesomeIcon icon={faBarsSort} />} isDarkVersion onChange={option => handleSelectedOrderChange(option)} value={selectedOrder} data-sentry-element="SimpleSelect" data-sentry-source-file="skill-details.tsx" />
              </span>
            </StyledTabsFilterWrapper>
            {isNewRoomsEnabled && <StyledFlex justifyContent="flex-start" gap="0.8rem">
                <StyledTypeButton variant="outlined" color="secondary" size="small" onClick={() => handleSelectedTypeChange('all')} $isSelected={selectedType === 'all'}>
                  All
                </StyledTypeButton>
                <StyledTypeButton variant="outlined" color="secondary" size="small" onClick={() => handleSelectedTypeChange(RoomType.WALKTHROUGH)} $isSelected={selectedType === RoomType.WALKTHROUGH}>
                  <FontAwesomeIcon icon={faRoute} /> Walkthroughs
                </StyledTypeButton>
                <StyledTypeButton variant="outlined" color="secondary" size="small" onClick={() => handleSelectedTypeChange(RoomType.CHALLENGE)} $isSelected={selectedType === RoomType.CHALLENGE}>
                  <FontAwesomeIcon icon={faFlag} /> Challenges
                </StyledTypeButton>
              </StyledFlex>}
          </StyledFilterHeader>
          <TabPanel value="all" data-sentry-element="TabPanel" data-sentry-source-file="skill-details.tsx">
            <RoomsList rooms={filteredRooms} isFetching={isFetching} data-sentry-element="RoomsList" data-sentry-source-file="skill-details.tsx" />
          </TabPanel>
          <TabPanel value="to-complete" data-sentry-element="TabPanel" data-sentry-source-file="skill-details.tsx">
            <RoomsList rooms={filteredToCompleteRooms} isFetching={isFetching} data-sentry-element="RoomsList" data-sentry-source-file="skill-details.tsx" />
          </TabPanel>
        </StyledTab>
      </div>
    </SlidingPane>;
};